import { computed, ref, unref, watch } from 'vue';

const MAX_SEAT_COUNT = 100;

const calculateDiscount = (subtotal, voucher) => {
  if (!voucher || subtotal == null) return 0;
  if (voucher.discount_type === 'percentage') {
    return (subtotal * Number(voucher.discount_value)) / 100;
  }
  return Math.min(Number(voucher.discount_value), subtotal);
};

const voucherAppliesToPlan = (voucher, plan) => {
  if (!voucher || !plan) return false;
  const slugs = voucher.applicable_plan_slugs;
  if (!slugs?.length) return true;
  return slugs.includes(plan.slug);
};

export function usePlanCardSeatPricing(
  planRef,
  durationRef,
  voucherRef,
  usageRef = null
) {
  const agentsCount = ref(0);
  const inboxesCount = ref(0);

  const baseAgents = computed(() => Number(planRef.value?.limits?.agents) || 0);
  const baseInboxes = computed(
    () => Number(planRef.value?.limits?.inboxes) || 0
  );

  const agentsUsed = computed(() => {
    const usage = unref(usageRef);
    return Number(usage?.agents) || 0;
  });

  const inboxesUsed = computed(() => {
    const usage = unref(usageRef);
    return Number(usage?.inboxes) || 0;
  });

  const minAgents = computed(() =>
    Math.max(baseAgents.value, agentsUsed.value)
  );
  const minInboxes = computed(() =>
    Math.max(baseInboxes.value, inboxesUsed.value)
  );

  const syncCountsToMinimums = () => {
    agentsCount.value = minAgents.value;
    inboxesCount.value = minInboxes.value;
  };

  watch(
    () => [planRef.value?.id, minAgents.value, minInboxes.value],
    () => syncCountsToMinimums(),
    { immediate: true }
  );

  const buildOptions = (min, max) => {
    const cappedMax = Math.min(max, MAX_SEAT_COUNT);
    if (min > cappedMax) {
      return [{ value: min, label: String(min) }];
    }
    return Array.from({ length: cappedMax - min + 1 }, (_, i) => {
      const value = min + i;
      return { value, label: String(value) };
    });
  };

  const hasExtraAgentPricing = computed(
    () => Number(durationRef.value?.extra_agent_price_per_unit) > 0
  );
  const hasExtraInboxPricing = computed(
    () => Number(durationRef.value?.extra_inbox_price_per_unit) > 0
  );

  const agentOptions = computed(() =>
    buildOptions(
      minAgents.value,
      hasExtraAgentPricing.value ? MAX_SEAT_COUNT : minAgents.value
    )
  );
  const inboxOptions = computed(() =>
    buildOptions(
      minInboxes.value,
      hasExtraInboxPricing.value ? MAX_SEAT_COUNT : minInboxes.value
    )
  );

  const extraAgents = computed(() =>
    Math.max(0, Number(agentsCount.value) - baseAgents.value)
  );
  const extraInboxes = computed(() =>
    Math.max(0, Number(inboxesCount.value) - baseInboxes.value)
  );

  const totalAmount = computed(() => {
    const duration = durationRef.value;
    if (!duration || duration.error) return null;

    const base = Number(duration.amount);
    if (!Number.isFinite(base)) return null;

    const agentUnit = Number(duration.extra_agent_price_per_unit) || 0;
    const inboxUnit = Number(duration.extra_inbox_price_per_unit) || 0;

    return (
      base + extraAgents.value * agentUnit + extraInboxes.value * inboxUnit
    );
  });

  const formatPrice = amount => {
    const duration = durationRef.value;
    if (amount == null || !duration) return null;
    const symbol = duration.currency_symbol || duration.currency_code || '';
    const formatted = Number(amount).toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
    return `${symbol} ${formatted}`.trim();
  };

  const voucherApplies = computed(() =>
    voucherAppliesToPlan(voucherRef?.value, planRef.value)
  );

  const discountedAmount = computed(() => {
    if (!voucherApplies.value || totalAmount.value == null) return null;
    const discount = calculateDiscount(totalAmount.value, voucherRef.value);
    return Math.max(0, totalAmount.value - discount);
  });

  const priceLabel = computed(() =>
    formatPrice(discountedAmount.value ?? totalAmount.value)
  );

  const originalPriceLabel = computed(() => {
    if (discountedAmount.value == null) return null;
    return formatPrice(totalAmount.value);
  });

  return {
    agentsCount,
    inboxesCount,
    baseAgents,
    baseInboxes,
    minAgents,
    minInboxes,
    agentOptions,
    inboxOptions,
    extraAgents,
    extraInboxes,
    priceLabel,
    originalPriceLabel,
    voucherApplies,
  };
}
